import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { SectionHeader, EmptyState, Spinner, DataTable } from '../../components/ui';
import { brokerApi } from '../../api/brokerClient';
import { ROUTES } from '../../routes/constants';
import { fmtValue, fmtTime, normalizeOrderRow } from './bookUtils';

function pickOrderValue(row, keys) {
  if (!row || typeof row !== 'object') return null;
  for (const key of keys) {
    const value = row[key];
    if (value !== undefined && value !== null && value !== '') return value;
  }
  return null;
}

function pickCellValue(value, row, fallbackKeys) {
  return pickOrderValue({ _value: value, ...(row || {}) }, ['_value', ...fallbackKeys]);
}

function cleanOrderToken(value) {
  if (value === undefined || value === null || value === '') return '';
  return String(value).trim().toUpperCase();
}

function normalizeOrderSide(value) {
  const token = cleanOrderToken(value);
  if (!token) return { label: '—', group: 'unknown' };
  if (['BUY', 'B', 'LONG'].includes(token)) return { label: 'BUY', group: 'buy' };
  if (['SELL', 'S', 'SHORT'].includes(token)) return { label: 'SELL', group: 'sell' };
  return { label: token, group: 'unknown' };
}

const SUCCESS_STATUSES = new Set([
  'SUCCESS',
  'FILLED',
  'FILL',
  'COMPLETE',
  'COMPLETED',
  'TRADED',
  'EXECUTED',
  'FULLY_FILLED',
  'FULLY FILLED',
]);

const PENDING_STATUSES = new Set([
  'PENDING',
  'OPEN',
  'SUBMITTED',
  'PART_FILLED',
  'PARTIALLY_FILLED',
  'PARTIAL',
  'PARTIAL_FILL',
  'TRIGGER_PENDING',
  'NEW',
  'REQUESTED',
]);

const CANCELLED_STATUSES = new Set([
  'CANCELLED',
  'CANCELED',
  'CLOSED',
  'EXPIRED',
]);

const REJECTED_STATUSES = new Set([
  'REJECTED',
  'FAILED',
  'ERROR',
  'CANCEL_REJECTED',
  'RMS_REJECTED',
]);

function normalizeOrderStatus(value) {
  const token = cleanOrderToken(value);
  if (!token) return { label: '—', group: 'unknown' };
  if (SUCCESS_STATUSES.has(token)) return { label: token, group: 'success' };
  if (PENDING_STATUSES.has(token)) return { label: token, group: 'pending' };
  if (CANCELLED_STATUSES.has(token)) return { label: token, group: 'cancelled' };
  if (REJECTED_STATUSES.has(token)) return { label: token, group: 'rejected' };
  return { label: token, group: 'unknown' };
}

const BADGE_CLASS = 'inline-flex items-center rounded-full border px-2 py-0.5 text-2xs font-semibold uppercase tracking-wide whitespace-nowrap';

function OrderSideBadge({ value }) {
  const meta = normalizeOrderSide(value);
  const tone =
    meta.group === 'buy'
      ? 'bg-bull/10 border-bull/30 text-bull'
      : meta.group === 'sell'
        ? 'bg-bear/10 border-bear/30 text-bear'
        : 'bg-bg-hover border-bg-border text-txt-secondary';

  return <span className={`${BADGE_CLASS} ${tone}`}>{meta.label}</span>;
}

function OrderStatusBadge({ value }) {
  const meta = normalizeOrderStatus(value);
  const tone =
    meta.group === 'success'
      ? 'bg-bull/10 border-bull/30 text-bull'
      : meta.group === 'pending'
        ? 'bg-amber-500/10 border-amber-500/30 text-amber-300'
        : meta.group === 'rejected'
          ? 'bg-bear/10 border-bear/30 text-bear'
          : 'bg-bg-hover border-bg-border text-txt-secondary';

  return <span className={`${BADGE_CLASS} ${tone}`}>{meta.label}</span>;
}

function BrokerOrderCard({ row }) {
  const side = pickCellValue(row.side, row, ['transaction_type', 'transactionType', 'action']);
  const status = pickCellValue(row.status, row, ['order_status', 'orderStatus', 'status_text']);

  return (
    <div className="rounded-md border border-bg-border bg-bg-surface/40 p-1.5">
      <div className="flex items-center justify-between gap-2 mb-1">
        <span className="font-medium text-txt-primary truncate min-w-0">{fmtValue(row.symbol)}</span>
        <OrderStatusBadge value={status} />
      </div>

      <div className="flex items-center flex-wrap gap-1.5 text-2xs font-mono text-txt-secondary mb-1">
        <OrderSideBadge value={side} />
        <span>Qty {fmtValue(row.qty)}</span>
        <span className="text-txt-muted">·</span>
        <span>Filled {fmtValue(row.filledQty)}</span>
      </div>

      <div className="grid grid-cols-2 gap-x-2 gap-y-0.5 text-2xs font-mono">
        <div className="min-w-0">
          <span className="text-txt-label">Type </span>
          <span className="text-txt-secondary">{fmtValue(row.type)}</span>
        </div>
        <div className="min-w-0">
          <span className="text-txt-label">Product </span>
          <span className="text-txt-secondary">{fmtValue(row.product)}</span>
        </div>
        <div className="min-w-0">
          <span className="text-txt-label">Price </span>
          <span className="text-txt-secondary">{fmtValue(row.price)}</span>
        </div>
        <div className="min-w-0">
          <span className="text-txt-label">Time </span>
          <span className="text-txt-secondary">{fmtTime(row.time)}</span>
        </div>
      </div>

      <div className="mt-0.5 text-xs font-mono text-txt-muted break-all" title={row.orderId || undefined}>
        Order ID <span className="text-txt-secondary">{fmtValue(row.orderId)}</span>
      </div>
    </div>
  );
}

export function OrderbookBody({ onRefreshChange } = {}) {
  const navigate = useNavigate();
  const [tab, setTab] = useState('live');

  const live = useQuery({
    queryKey: ['brokerOrderbookPage'],
    queryFn: brokerApi.getOrderbook,
    staleTime: 10_000,
    refetchInterval: 15_000,
    retry: 1,
    enabled: tab === 'live',
  });

  const paper = useQuery({
    queryKey: ['paperOrderbook'],
    queryFn: brokerApi.getPaperOrderbook,
    staleTime: 10_000,
    refetchInterval: 30_000,
    retry: 1,
    enabled: tab === 'paper',
  });

  const active = tab === 'live' ? live : paper;
  const rows = Array.isArray(active.data?.orders) ? active.data.orders.map(normalizeOrderRow) : [];
  const handleRefresh = useCallback(() => {
    if (tab === 'live') return live.refetch();
    return paper.refetch();
  }, [tab, live.refetch, paper.refetch]);

  useEffect(() => {
    onRefreshChange?.(handleRefresh);
    return () => onRefreshChange?.(null);
  }, [onRefreshChange, handleRefresh]);

  const liveColumns = [
    { key: 'orderId', label: 'Order ID', render: (v) => fmtValue(v) },
    { key: 'symbol', label: 'Symbol', render: (v) => fmtValue(v) },
    { key: 'side', label: 'Side', render: (v, row) => <OrderSideBadge value={pickCellValue(v, row, ['transaction_type', 'transactionType', 'action'])} /> },
    { key: 'product', label: 'Product', render: (v) => fmtValue(v) },
    { key: 'type', label: 'Type', render: (v) => fmtValue(v) },
    { key: 'qty', label: 'Qty', render: (v) => fmtValue(v) },
    { key: 'filledQty', label: 'Filled', render: (v) => fmtValue(v) },
    { key: 'price', label: 'Price', render: (v) => fmtValue(v) },
    { key: 'status', label: 'Status', render: (v, row) => <OrderStatusBadge value={pickCellValue(v, row, ['order_status', 'orderStatus', 'status_text'])} /> },
    { key: 'time', label: 'Time', render: (v) => fmtTime(v) },
  ];

  const fmtPaperId = (v) => {
    if (!v || typeof v !== 'string') return fmtValue(v);
    return v.length > 14 ? v.slice(0, 14) + '…' : v;
  };

  const paperColumns = [
    { key: 'symbol', label: 'Symbol', render: (v) => fmtValue(v) },
    { key: 'side', label: 'Side', render: (v, row) => <OrderSideBadge value={pickCellValue(v, row, ['transaction_type', 'transactionType', 'action'])} /> },
    { key: 'type', label: 'Type', render: (v) => fmtValue(v) },
    { key: 'qty', label: 'Qty', render: (v) => fmtValue(v) },
    { key: 'filledQty', label: 'Filled', render: (v) => fmtValue(v) },
    { key: 'price', label: 'Price', render: (v) => fmtValue(v) },
    { key: 'status', label: 'Status', render: (v, row) => <OrderStatusBadge value={pickCellValue(v, row, ['order_status', 'orderStatus', 'status_text'])} /> },
    { key: 'time', label: 'Time', render: (v) => fmtTime(v) },
    { key: 'orderId', label: 'Order ID', render: fmtPaperId, mobileHidden: true },
  ];

  const columns = tab === 'live' ? liveColumns : paperColumns;

  return (
    <>
      {/* ── Tab Bar ──────────────────────────────────────────────── */}
      <div className="flex gap-1 bg-bg-hover/30 rounded-lg p-1 w-fit">
        <button
          onClick={() => setTab('live')}
          className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${
            tab === 'live'
              ? 'bg-bg-surface text-txt-primary shadow-sm'
              : 'text-txt-muted hover:text-txt-secondary'
          }`}
        >
          Live
        </button>
        <button
          onClick={() => setTab('paper')}
          className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${
            tab === 'paper'
              ? 'bg-amber/15 text-amber shadow-sm'
              : 'text-txt-muted hover:text-txt-secondary'
          }`}
        >
          Paper
        </button>
      </div>

      {/* ── Table ────────────────────────────────────────────────── */}
      <div className="rounded-lg border border-transparent bg-transparent p-0 lg:border-bg-border lg:bg-bg-panel lg:p-4 lg:shadow-panel">
        {active.isLoading ? (
          <div className="flex justify-center py-12"><Spinner size={18} /></div>
        ) : active.error ? (
          <EmptyState title="Could not load orderbook" description={active.error.message} />
        ) : rows.length === 0 ? (
          <EmptyState
            title={tab === 'live' ? 'No broker orders yet' : 'No paper orders yet'}
            description={tab === 'live'
              ? 'Orders placed through your connected broker will appear here once a live strategy executes.'
              : 'Paper orders from your deployed strategies will appear here as they fire.'}
            action={(
              <button onClick={() => navigate(ROUTES.STRATEGIES)} className="btn-secondary mt-4 text-xs">
                View strategies
              </button>
            )}
          />
        ) : (
          <>
            <div className="lg:hidden space-y-1.5">
              {rows.map((row, i) => (
                <BrokerOrderCard key={row.id ?? i} row={row} />
              ))}
            </div>
            <div className="hidden lg:block">
              <DataTable columns={columns} data={rows} />
            </div>
          </>
        )}
      </div>
    </>
  );
}

export default function BrokerOrderbookPage() {
  return (
    <div className="max-w-7xl space-y-5">
      <SectionHeader
        title="Orderbook"
        subtitle="Live broker orders and paper trade orders."
      />
      <OrderbookBody />
    </div>
  );
}
